/**
 * useReCaptchaV3 Hook
 *
 * Manages reCAPTCHA v3 integration for invisible bot protection.
 * Handles script loading, token generation, and error states.
 *
 * reCAPTCHA v3 is score-based and invisible (no checkbox UI).
 * Tokens expire in 2 minutes and should be generated just before API calls.
 *
 * @module useReCaptchaV3
 */

import { useState, useEffect, useCallback } from 'react'

declare global {
    interface Window {
        grecaptcha?: {
            ready: (callback: () => void) => void
            execute: (
                siteKey: string,
                options: { action: string },
            ) => Promise<string>
        }
    }
}

export interface ReCaptchaConfig {
    siteKey: string
    onError?: (error: Error) => void
}

export interface UseReCaptchaV3Return {
    isReady: boolean
    isLoading: boolean
    error: Error | null
    execute: (action: string) => Promise<string>
}

export const useReCaptchaV3 = (
    config: ReCaptchaConfig,
): UseReCaptchaV3Return => {
    const { siteKey, onError } = config

    const [isReady, setIsReady] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        if (window.grecaptcha) {
            window.grecaptcha.ready(() => {
                setIsReady(true)
                setIsLoading(false)
            })
            return
        }

        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
        script.async = true
        script.defer = true

        script.onload = () => {
            if (window.grecaptcha) {
                window.grecaptcha.ready(() => {
                    setIsReady(true)
                    setIsLoading(false)
                })
            }
        }

        script.onerror = () => {
            const loadError = new Error('Failed to load reCAPTCHA script')
            setError(loadError)
            setIsLoading(false)

            if (onError) {
                onError(loadError)
            }
        }

        document.head.appendChild(script)

        return () => {
            // Script remains loaded for session
        }
    }, [siteKey, onError])

    const execute = useCallback(
        async (action: string): Promise<string> => {
            if (!isReady || !window.grecaptcha) {
                const notReadyError = new Error(
                    'reCAPTCHA not loaded or not ready',
                )
                setError(notReadyError)
                throw notReadyError
            }

            try {
                const token = await window.grecaptcha.execute(siteKey, {
                    action,
                })
                return token
            } catch (err) {
                const executeError =
                    err instanceof Error
                        ? err
                        : new Error('Failed to execute reCAPTCHA')

                setError(executeError)

                if (onError) {
                    onError(executeError)
                }

                throw executeError
            }
        },
        [isReady, siteKey, onError],
    )

    return {
        isReady,
        isLoading,
        error,
        execute,
    }
}
